import { TextField } from 'components/inputs/text-field';
import { FC, KeyboardEvent, useCallback, useMemo, useState } from 'react';

interface EmailSelectProps {
  email?: string | null;
  setEmail: (email: string) => void;
}

const EmailSelect: FC<EmailSelectProps> = ({ email: customerEmail, setEmail: setCustomerEmail }) => {
  const [input, setInput] = useState<string>('');

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const emails: Set<string> = useMemo(() => {
    return !customerEmail ? new Set() : new Set(customerEmail.replace(/,/g, ' ').trim().split(/\s+/));
  }, [customerEmail]);

  const handleSelect = useCallback(
    (email: string) => {
      const emailsCopy = new Set(emails);
      if (emailsCopy.delete(email)) {
        setInput(email);
      } else {
        emailsCopy.add(email);
        setInput('');
      }
      setCustomerEmail([...emailsCopy.values()].join(','));
    },
    [emails, setCustomerEmail, setInput]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (input && !emails.has(input)) {
          handleSelect(input);
        }
      }
    },
    [emails, input, handleSelect]
  );

  return (
    <div className="grid-4 mt-2">
      <div className="d-flex g-2">
        <TextField
          inputMode="email"
          value={input}
          onChange={setInput}
          onKeyDown={handleKeyDown}
          allowLowercase
          data-cy="customer-email-input"
        />
        <button
          className="btn-outline d-flex align-items-center"
          type="button"
          disabled={!input || emails.has(input) || !emailRegex.test(input)}
          onClick={() => handleSelect(input)}
          data-cy="customer-email-add-button"
        >
          <span className="material-icons icon-large">add</span>
        </button>
      </div>
      <div className="col-span-3 d-flex flex-wrap g-2">
        {[...emails.values()].map(email => (
          <div className="tag g-2" key={email}>
            <p className="text-primary">{email}</p>
            <button type="button" className="tag-close" onClick={() => handleSelect(email)}>
              <span className="material-icons">close</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmailSelect;
